







































































































































/* General */
import ItemsListingLayout from '@/layouts/items-listing-layout.vue'
import moment from 'moment'

/* Mixins */
import EntityLandingMixin from '@/mixins/entity-landing.mixin'

/* Components */
import ProjectHomeHeaderOptions from './components/project-home-header-options.vue'

/* Helpers */
import LocalStore from '@/helpers/localstore'
import RouteAssistant from '@/helpers/route-assistant'
import BreadcrumbAssistant from '@/helpers/breadcrumb-assistant'
import TextAssistant from '@/helpers/text-assistant'
import ItemFilterAssistant from '@/helpers/item-filter-assistant'

/* Data types */
import { EEntities, SortOptionValues } from '@/types'
import { IBreadcrumbItemMenuItem } from '@/types/data-objects/items'
import { IItemTraitSet } from '@/types/data-objects/listing'
import {
  IInternalProjectsOutputFilterOptionsForListing,
  IKanbanColumnOrdering,
  IStatusForSelection,
  ITaskForKanban,
  IWorkflowDetailStatusTransition,
} from '@/api/types/response.types'
import {
  IInternalProjectsInputManageCustomFilter,
  IInternalProjectsInputUpdateCustomFilter,
  ITasksInputUpdateForKanban,
} from '@/api/types/request.types'

/* API */
import InternalProjectsAPI from '@/api/modules/internal-projects.api'
import TasksAPI from '@/api/modules/tasks.api'
import CustomFiltersAPI from '@/api/modules/custom-filters.api'

export default EntityLandingMixin.extend({
  name: 'ProjectKanban',

  components: {
    ItemsListingLayout,
    ProjectHomeHeaderOptions,
  },

  data() {
    return {
      RouteAssistant,
      maxTasks: 5000,
      initialKanbanDataLoaded: false,
      showAddTaskDrawer: false,
      isHandlingMove: false,
      columnOrderings: [] as Array<IKanbanColumnOrdering>,
      tasks: [] as Array<ITaskForKanban>,
      statusesForColumns: [] as Array<IStatusForSelection>,
      workflowStatusTransitions: [] as Array<IWorkflowDetailStatusTransition>,
      showUnsortedTasksOnTopForKanban: false,
    }
  },

  watch: {
    showAddTaskDrawer: {
      async handler(show: boolean) {
        // Whenever the drawer is toggled refresh the board.
        // ********
        // We do so when OPENING to re-sync the columnOrderings with any drag n' drop changes made since the last data load.
        // Otherwise, upon adding a new task, the old columnOrderings would get re-applied because KanbanBoard would refresh the columns
        // due to the task being pushed to the tasks list.
        // ********
        // We do so when CLOSING to accurately reflect filterMatches and filter counts.
        await this.refreshKanbanBoard()
        if (!show) {
          // @ts-ignore
          this.$refs.kanbanBoard.clearNewTasks()
        }
      },
    },
  },

  beforeDestroy() {
    this.endPolling()
  },

  computed: {
    noTasks(): boolean {
      return this.initialKanbanDataLoaded && this.tasks.length === 0
    },
    tooManyTasks(): boolean {
      return this.tasks.length > this.maxTasks
    },
  },

  async created() {
    LocalStore.setProjectDefaultedToKanban(this.id)
    this.showUnsortedTasksOnTopForKanban = LocalStore.kanbanUnsortedTasksOnTop

    this.isLoadingInitialData = true
    await Promise.all([
      this.fetchDetails(),
      this.fetchFilterOptionsForListing(),
      this.getStatusesForColumns(),
      this.getWorkflowStatusTransitions(),
    ])
    this.isLoadingInitialData = false
    this.initialKanbanDataLoaded = true
    LocalStore.lastItemListRouteVisited = RouteAssistant.getProjectKanbanRoute(
      this.accountID,
      this.id,
      this.$permissions,
      this.name,
    )
    this.startPolling()
    this.postCreated()
    this.fetchDefaultFilterOptionCounts()
    await this.showInfoOnEntityLandingIfNecessary()
  },

  methods: {
    startPolling() {
      // Ensure we're clearing old polling, if necessary
      this.endPolling()
      this.poller = setInterval(async () => {
        try {
          await this.autoRefreshDataIfStale()
        } catch {
          this.endPolling()
        }
      }, this.pollIntervalInMS)
    },
    endPolling() {
      clearInterval(this.poller)
    },
    async handleUnsortedTasksOnTopToggle(val: boolean) {
      // Refresh board so that the column orderings are in sync before doing
      // the resorting on the kanban-board component. Otherwise, if you kept
      // clicking the toggle, the logic for kanbanBoard.orderTasksInColumn() would
      // pull a potentially old column orderings array and cards moved around would be
      // repositioned back.
      await this.refreshKanbanBoard()

      // Triggers kanban-board.orderTasksInColumn to update...
      this.showUnsortedTasksOnTopForKanban = val

      // Save local store variable...
      LocalStore.kanbanUnsortedTasksOnTop = val
    },
    async refreshKanbanBoard() {
      await Promise.all([
        this.fetchFilterOptionsForListing(),
        this.fetchResults(),
      ])
      this.fetchDefaultFilterOptionCounts()
    },
    async autoRefreshDataIfStale() {
      const lastUpdatedEpochTime = await InternalProjectsAPI.get_LastUpdatedEpochTime(
        this.id,
      )
      if (
        lastUpdatedEpochTime.epochTime &&
        this.lastAccessEpochTime < lastUpdatedEpochTime.epochTime
      ) {
        await Promise.all([
          this.fetchDetails(),
          this.fetchFilterOptionsForListing(),
          this.fetchResults(),
        ])
        this.fetchDefaultFilterOptionCounts()
        this.lastAccessEpochTime = lastUpdatedEpochTime.epochTime
      }
    },
    async fetchDetails() {
      try {
        const details = await InternalProjectsAPI.get_Detail(this.id)
        this.name = details.name
        this.members = details.listPeople
        this.workflow = details.workflow
      } catch (error) {
        this.$error.emit(error)
      }
    },
    async fetchResults() {
      const params = {
        priority_ids: TextAssistant.convertNumberArrayToStringList(
          this.itemTraitSet.priorityIDs,
        ),
        status_ids: TextAssistant.convertNumberArrayToStringList(
          this.itemTraitSet.statusIDs,
        ),
        status_type: this.itemTraitSet.statusType,
        assignee_ids: TextAssistant.convertNumberArrayToStringList(
          this.itemTraitSet.assigneeIDs,
        ),
        assigned_not_to_you: this.itemTraitSet.assignedNotToYou,
        job_appointee_groupings_string: this.itemTraitSet
          .jobAppointeeGroupingsString,
        including_tag_ids: TextAssistant.convertNumberArrayToStringList(
          this.itemTraitSet.includingTagIDs,
        ),
        not_including_tag_ids: TextAssistant.convertNumberArrayToStringList(
          this.itemTraitSet.notIncludingTagIDs,
        ),
        start_due_date: this.itemTraitSet.startDueDate,
        end_due_date: this.itemTraitSet.endDueDate,
        due_type: this.itemTraitSet.dueType,
        sort: this.itemTraitSet.sort,
        search_term: this.itemTraitSet.searchTerm,
        only_watching: this.itemTraitSet.onlyWatching,
        created_within_days: this.itemTraitSet.createdWithinDays,
        last_updated_within_days: this.itemTraitSet.lastUpdatedWithinDays,
        creator_ids: TextAssistant.convertNumberArrayToStringList(
          this.itemTraitSet.creatorIDs,
        ),
      }

      try {
        this.totalItemCount = null
        const response = await TasksAPI.get_SearchForKanban(
          this.id,
          params.creator_ids,
          params.priority_ids,
          params.status_ids,
          params.status_type,
          params.assignee_ids,
          params.assigned_not_to_you,
          params.job_appointee_groupings_string,
          params.including_tag_ids,
          params.not_including_tag_ids,
          params.due_type,
          params.start_due_date,
          params.end_due_date,
          params.search_term,
          params.only_watching,
          params.created_within_days,
          params.last_updated_within_days,
        )

        const filterMatches = response.listTasks.filter(t => t.filterMatch)

        this.totalItemCount = filterMatches.length
        this.columnOrderings = response.listKanbanColumnOrderings
        this.tasks = response.listTasks
        this.lastAccessEpochTime = response.accessEpochTime

        const breadCrumbItems = [] as IBreadcrumbItemMenuItem[]

        filterMatches.forEach(t => {
          breadCrumbItems.push({
            itemID: t.taskID,
            entityID: this.id,
            entityName: this.name,
            entityType: EEntities.PROJECTS,
            title: t.title,
            refNumber: t.refNumber,
          } as IBreadcrumbItemMenuItem)
        })

        BreadcrumbAssistant.cacheBreadcrumbItemMenuItems(
          this.$store,
          breadCrumbItems,
        )
      } catch (error) {
        LocalStore.clearHomeSavedQuery(this.id)
        this.$error.emit(error)
      }
    },

    async fetchFilterOptionsForListing() {
      try {
        const response = await InternalProjectsAPI.get_FilterOptionsForListing(
          this.id,
        )
        this.hydrateFilterOptionsForListing(response)
      } catch (error) {
        this.$error.emit(error)
      }
    },

    // Should not await this method as it is non-essential to the function of this screen.
    async fetchDefaultFilterOptionCounts() {
      try {
        // Swallow if timeout (or other error) occurs...
        const counts = await InternalProjectsAPI.get_DefaultFilterOptionCounts(
          this.id,
        )
        this.updateDefaultFilterOptionCounts(counts.listDefaultFilters)
      } catch (error) {
        // Swallow
      }
    },

    hydrateFilterOptionsForListing(
      data: IInternalProjectsOutputFilterOptionsForListing,
    ) {
      this.hydrateFilterOptionsForListing_Base(data)
      this.customFilterNavItems = data.listCustomFilters.map(c => ({
        value: ItemFilterAssistant.getCustomFilterIdentifier(c.id),
        label: c.name,
        icon: 'filter',
      }))
      this.customFilterItemTraitSets = data.listCustomFilters.map(c => ({
        id: c.id,
        itemTraitSet: {
          priorityIDs: c.listPriorityIDs ? c.listPriorityIDs : [],
          statusIDs: c.listStatusIDs ? c.listStatusIDs : [],
          statusType: c.statusType ? c.statusType : '',
          assigneeIDs: c.listAssigneeIDs ? c.listAssigneeIDs : [],
          assignedNotToYou: c.assignedNotToYou,
          jobAppointeeGroupingsString: ItemFilterAssistant.convertJobAppointeeGroupingsToStringRepresentation(
            c.listJobAppointeeGroupings,
          ),
          dueType: c.dueType ? c.dueType : null,
          includingTagIDs: c.listIncludingTagIDs ? c.listIncludingTagIDs : [],
          notIncludingTagIDs: c.listNotIncludingTagIDs
            ? c.listNotIncludingTagIDs
            : [],
          startDueDate: c.startDueDate
            ? moment(c.startDueDate).format('YYYY-MM-DD')
            : null,
          endDueDate: c.endDueDate
            ? moment(c.endDueDate).format('YYYY-MM-DD')
            : null,
          searchTerm: c.searchTerm ? c.searchTerm : '',
          onlyWatching: c.onlyWatching,
          createdWithinDays: c.createdWithinDays,
          lastUpdatedWithinDays: c.lastUpdatedWithinDays,
          creatorIDs: c.listCreatorIDs ? c.listCreatorIDs : [],
          customerIDs: [], // Never used, so default it (so the comparison is equal in isItemTraitSetEquivalent()
          isSpam: false, // Never used, so default it (so the comparison is equal in isItemTraitSetEquivalent()
          sort: SortOptionValues.UPDATED_RECENT,
          projectIDs: [], // Unused for mailbox/project home, default it so the comparison is equal in isItemTraitSetEquivalent()
          mailboxIDs: [], // Unused for mailbox/project home, default it so the comparison is equal in isItemTraitSetEquivalent()
        } as IItemTraitSet,
      }))
    },

    async getMembers() {
      try {
        this.members = await InternalProjectsAPI.get_Members(this.id)
      } catch (error) {
        this.$error.emit(error, true)
      }
    },

    async createCustomFilter(name: string) {
      try {
        this.isCustomFilterSubmitting = true
        const formData = {
          name,
          listCreatorIDs: this.itemTraitSet.creatorIDs,
          listAssigneeIDs: this.itemTraitSet.assigneeIDs,
          listJobAppointeeGroupings: ItemFilterAssistant.convertStringToJobAppointeeGroupingsRepresentation(
            this.itemTraitSet.jobAppointeeGroupingsString,
          ),
          listIncludingTagIDs: this.itemTraitSet.includingTagIDs,
          listNotIncludingTagIDs: this.itemTraitSet.notIncludingTagIDs,
          listStatusIDs: this.itemTraitSet.statusIDs,
          listPriorityIDs: this.itemTraitSet.priorityIDs,
          createdWithinDays: this.itemTraitSet.createdWithinDays,
          lastUpdatedWithinDays: this.itemTraitSet.lastUpdatedWithinDays,
          assignedNotToYou: this.itemTraitSet.assignedNotToYou,
          statusType: this.itemTraitSet.statusType,
          dueType: this.itemTraitSet.dueType,
          startDueDate: this.itemTraitSet.startDueDate,
          endDueDate: this.itemTraitSet.endDueDate,
          searchTerm: this.itemTraitSet.searchTerm,
          onlyWatching: this.itemTraitSet.onlyWatching,
        } as IInternalProjectsInputManageCustomFilter
        const response = await InternalProjectsAPI.post_CreateCustomFilter(
          this.id,
          formData,
        )
        await this.fetchFilterOptionsForListing()
      } catch (error) {
        this.$error.emit(error, true)
      } finally {
        this.isCustomFilterSubmitting = false
      }
    },

    async updateCustomFilter(name: string) {
      try {
        this.isCustomFilterSubmitting = true
        const formData = {
          name,
          listCreatorIDs: this.itemTraitSet.creatorIDs,
          listAssigneeIDs: this.itemTraitSet.assigneeIDs,
          listJobAppointeeGroupings: ItemFilterAssistant.convertStringToJobAppointeeGroupingsRepresentation(
            this.itemTraitSet.jobAppointeeGroupingsString,
          ),
          listIncludingTagIDs: this.itemTraitSet.includingTagIDs,
          listNotIncludingTagIDs: this.itemTraitSet.notIncludingTagIDs,
          listStatusIDs: this.itemTraitSet.statusIDs,
          listPriorityIDs: this.itemTraitSet.priorityIDs,
          createdWithinDays: this.itemTraitSet.createdWithinDays,
          lastUpdatedWithinDays: this.itemTraitSet.lastUpdatedWithinDays,
          assignedNotToYou: this.itemTraitSet.assignedNotToYou,
          statusType: this.itemTraitSet.statusType,
          dueType: this.itemTraitSet.dueType,
          startDueDate: this.itemTraitSet.startDueDate,
          endDueDate: this.itemTraitSet.endDueDate,
          searchTerm: this.itemTraitSet.searchTerm,
          onlyWatching: this.itemTraitSet.onlyWatching,
        } as IInternalProjectsInputUpdateCustomFilter
        const response = await InternalProjectsAPI.put_UpdateCustomFilter(
          this.id,
          ItemFilterAssistant.getIDOfFilterIdentifier(
            this.currentFilterNavItem.value,
          ),
          formData,
        )
        await this.fetchFilterOptionsForListing()
      } catch (error) {
        this.$error.emit(error, true)
      } finally {
        this.isCustomFilterSubmitting = false
      }
    },

    async deleteCustomFilter(identifier: string) {
      try {
        const response = await CustomFiltersAPI.delete_Delete(
          ItemFilterAssistant.getIDOfFilterIdentifier(identifier),
        )

        this.fetchFilterOptionsForListing()
        if (this.currentFilterNavItem.value !== identifier) {
          this.$messaging.showSuccess(response.message)
        }
      } catch (error) {
        this.$error.emit(error, true)
      }
    },

    /* Kanban specific */
    async getStatusesForColumns() {
      try {
        this.statusesForColumns = await InternalProjectsAPI.get_Statuses(
          this.id,
        )
      } catch (error) {
        this.$error.emit(error)
      }
    },

    async getWorkflowStatusTransitions() {
      try {
        this.workflowStatusTransitions = await InternalProjectsAPI.get_WorkflowStatusTransitions(
          this.id,
        )
      } catch (error) {
        this.$error.emit(error)
      }
    },

    async handleTaskMove(
      taskID: number,
      statusID: number,
      listTaskIDsColumnOrder: Array<number>,
    ) {
      try {
        this.isHandlingMove = true
        const data = {
          lastAccessEpochTime: this.lastAccessEpochTime,
          statusID,
          listTaskIDsColumnOrder,
        } as ITasksInputUpdateForKanban
        const response = await TasksAPI.put_UpdateForKanban(
          this.id,
          taskID,
          data,
        )
        /* Set the new last access time */
        this.lastAccessEpochTime = response.lastUpdatedEpochTimeAfterUpdate

        if (response.needsRefresh) {
          /* Refresh the board if we were previously out of date */
          await this.refreshKanbanBoard()
        } else {
          const movedTaskToNewStatus =
            this.tasks.find(t => t.taskID === taskID).statusID !== statusID

          if (movedTaskToNewStatus) {
            /* 
            Always refetching results after a *move to a new status* for the following reasons:
            This.Tasks will be rehydrated which will trigger refreshing the <kanban-board> via its watch which also causes: 

            (1) If assignee is updated due to status change on a project with jobs, avatar updated
            (2) Current filter count will be in sync
            (3) Timestamp on moved card (and other cards) will be updated
            (4) "Matches filter" transparency will reflect correctly

            Note: The ColumnOrderings array is also updated as well in fetchResults(), but that doesn't *actually* matter
            given that the cards will already be in the correct order based on the manual moves.

            We don't call refreshKanbanBoard() here to save ourselves calling the other API methods bound to that method
            (and we know the the board otherwise was not outdated because we've passed the original if above.)

            Really, we just need the stuff from fetchResults()!
          */
            await this.fetchResults()
          }
        }
      } catch (error) {
        this.$error.emit(error, true)
        /* Always reload the data on error (likely task doesn't exist anymore) */
        await this.refreshKanbanBoard()
      } finally {
        this.isHandlingMove = false
      }
    },

    /* Manually pushes the task to the board and updates last access time */
    async addTaskToKanbanBoard(task: ITaskForKanban, newEpochTime: number) {
      this.lastAccessEpochTime = newEpochTime
      this.tasks.push(task)
      // @ts-ignore
      this.$refs.kanbanBoard.scrollToNewTask(task.taskID)
    },
  },
})
